import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  ConsentChecks, DonePanel, Field, GoldButton, Slider, ToolShell,
  fmt, fmtPct, inputCls, parseNum, selectCls,
} from "@/components/FormUI";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEGAL = "Simulación orientativa basada en los datos introducidos. Supone que los costes variables evolucionan en proporción al volumen y que los costes fijos se mantienen constantes. El impacto en valor es una aproximación por múltiplos sectoriales, no una valoración.";

const SECTORES = [
  "Industrial y manufactura", "Distribución y comercio mayorista", "Construcción",
  "Ingeniería y servicios técnicos", "Servicios profesionales", "Tecnología y software",
  "Salud y servicios sanitarios", "Transporte y logística", "Alimentación y bebidas",
  "Hostelería y restauración", "Comercio minorista", "Medioambiente y gestión de residuos",
  "Alquiler de maquinaria y equipos", "Educación y formación", "Otros",
];

const H_ROWS = [-20, -10, 0, 10, 20];
const H_COLS = [-10, -5, 0, 5, 10];

export default function Sensibilidad() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ sector: SECTORES[0], ventas: "", aprov: "", otros: "", personal: "", fijos: "" });
  const [base, setBase] = useState(null);
  const [sliders, setSliders] = useState({ vol: 0, precio: 0, aprov: 0, personal: 0 });
  const [lead, setLead] = useState({ nombre: "", empresa: "", email: "", telefono: "", consent_privacidad: false, consent_comercial: false });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [leadLoading, setLeadLoading] = useState(false);

  useEffect(() => {
    document.title = "Simulador de márgenes | AJG M&A Consulting";
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const payload = () => ({
    sector: form.sector,
    ventas: parseNum(form.ventas),
    aprovisionamientos: parseNum(form.aprov),
    otros_variables: form.otros ? parseNum(form.otros) : null,
    personal: parseNum(form.personal),
    fijos: parseNum(form.fijos),
  });

  const analyze = async (e) => {
    e.preventDefault();
    const p = payload();
    if (!(p.ventas > 0) || Number.isNaN(p.aprovisionamientos) || Number.isNaN(p.personal) || Number.isNaN(p.fijos)) {
      toast.error("Completa ventas, aprovisionamientos, personal y gastos fijos.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/sensibilidad/analyze`, p);
      setBase(data);
      setSliders({ vol: 0, precio: 0, aprov: 0, personal: 0 });
      setDone(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo calcular.");
    } finally {
      setLoading(false);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.consent_privacidad) {
      toast.error("Debes aceptar la política de privacidad.");
      return;
    }
    setLeadLoading(true);
    try {
      await axios.post(`${API}/sensibilidad/lead`, {
        datos: payload(),
        nombre: lead.nombre, empresa: lead.empresa, email: lead.email, telefono: lead.telefono,
        consent_privacidad: lead.consent_privacidad, consent_comercial: lead.consent_comercial,
      });
      setDone(true);
    } catch {
      toast.error("No se pudo enviar. Inténtalo de nuevo.");
    } finally {
      setLeadLoading(false);
    }
  };

  const sim = base ? (() => {
    const ventas = base.ventas * (1 + sliders.vol / 100) * (1 + sliders.precio / 100);
    const aprov = base.aprovisionamientos * (1 + sliders.vol / 100) * (1 + sliders.aprov / 100);
    const otros = base.otros_variables * (1 + sliders.vol / 100);
    const personal = base.personal * (1 + sliders.personal / 100);
    const mb = ventas - aprov - otros;
    const ebitda = mb - personal - base.fijos;
    const mc = ventas > 0 ? mb / ventas : 0;
    const pm = mc > 0 ? (personal + base.fijos) / mc : null;
    return { ventas, ebitda: Math.round(ebitda), margen: ventas > 0 ? (ebitda / ventas) * 100 : 0, pm, ms: pm ? ((ventas - pm) / ventas) * 100 : null };
  })() : null;

  const heatCell = (rv, ca) => {
    const ventas = base.ventas * (1 + rv / 100);
    const aprov = base.aprovisionamientos * (1 + rv / 100) * (1 + ca / 100);
    const otros = base.otros_variables * (1 + rv / 100);
    return Math.round(ventas - aprov - otros - base.personal - base.fijos);
  };

  return (
    <ToolShell
      eyebrow="Herramientas · Simulador de márgenes"
      title="¿Cómo afectan tus ventas y costes al resultado y al valor de tu empresa?"
      intro="Simula variaciones de volumen, precios y costes sobre tu EBITDA, tu punto muerto y el valor de tu compañía."
    >
      <div className="mt-14 grid gap-12 lg:grid-cols-2 lg:items-start">
        <form onSubmit={analyze} className="space-y-6" data-testid="sens-form">
          <Field label="Sector">
            <select data-testid="sens-sector" value={form.sector} onChange={set("sector")} className={selectCls}>
              {SECTORES.map((s) => <option key={s} value={s} className="bg-ink-2 text-white">{s}</option>)}
            </select>
          </Field>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Ventas anuales (€)">
              <input data-testid="sens-ventas" value={form.ventas} onChange={set("ventas")} inputMode="numeric" className={inputCls} />
            </Field>
            <Field label="Aprovisionamientos (€)">
              <input data-testid="sens-aprov" value={form.aprov} onChange={set("aprov")} inputMode="numeric" className={inputCls} />
            </Field>
            <Field label="Otros costes variables (€)" hint="Opcional: transporte, comisiones, energía variable...">
              <input data-testid="sens-otros" value={form.otros} onChange={set("otros")} inputMode="numeric" className={inputCls} />
            </Field>
            <Field label="Gastos de personal (€)">
              <input data-testid="sens-personal" value={form.personal} onChange={set("personal")} inputMode="numeric" className={inputCls} />
            </Field>
            <Field label="Otros gastos fijos (€)">
              <input data-testid="sens-fijos" value={form.fijos} onChange={set("fijos")} inputMode="numeric" className={inputCls} />
            </Field>
          </div>
          <GoldButton loading={loading} testid="sens-submit">Calcular mi situación</GoldButton>
        </form>

        <div className="lg:sticky lg:top-10">
          {base && sim ? (
            <div className="space-y-6" data-testid="sens-result">
              <div className="grid grid-cols-2 gap-4">
                {[
                  { l: "EBITDA actual", v: `${fmt(base.ebitda)} €` },
                  { l: "EBITDA simulado", v: `${fmt(sim.ebitda)} €`, gold: true },
                  { l: "Margen EBITDA simulado", v: fmtPct(Math.round(sim.margen * 10) / 10) },
                  { l: "Punto muerto simulado", v: sim.pm ? `${fmt(Math.round(sim.pm))} €` : "-" },
                  { l: "Margen de seguridad", v: sim.ms != null ? fmtPct(Math.round(sim.ms * 10) / 10) : "-" },
                  { l: "Apalancamiento operativo", v: base.apalancamiento ? String(base.apalancamiento).replace(".", ",") : "-" },
                ].map((c) => (
                  <div key={c.l} className="border border-gold/20 bg-ink-3/60 p-5" data-testid={`sens-card-${c.l}`}>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gold/80">{c.l}</p>
                    <p className={`mt-2 font-serif text-2xl ${c.gold ? "text-gold" : "text-white"}`}>{c.v}</p>
                  </div>
                ))}
              </div>
              <div className="border border-gold/20 bg-ink-3/60 p-7">
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">Simulador en tiempo real</p>
                <div className="mt-5 space-y-5">
                  <Slider label="Volumen de ventas" value={sliders.vol} min={-30} max={30} onChange={(v) => setSliders({ ...sliders, vol: v })} testid="sens-slider-vol" suffix=" %" />
                  <Slider label="Precio de venta" value={sliders.precio} min={-15} max={15} onChange={(v) => setSliders({ ...sliders, precio: v })} testid="sens-slider-precio" suffix=" %" />
                  <Slider label="Coste de aprovisionamientos" value={sliders.aprov} min={-20} max={20} onChange={(v) => setSliders({ ...sliders, aprov: v })} testid="sens-slider-aprov" suffix=" %" />
                  <Slider label="Gastos de personal" value={sliders.personal} min={-10} max={20} onChange={(v) => setSliders({ ...sliders, personal: v })} testid="sens-slider-personal" suffix=" %" />
                </div>
                <p className="mt-5 text-xs leading-relaxed text-slate-500">
                  Una subida de volumen aumenta ventas y costes variables en la misma proporción; una subida de
                  precio aumenta las ventas sin aumentar los costes variables.
                </p>
              </div>
              {base.frase_aprov5 != null && (
                <div className="border-l-2 border-gold/50 bg-gold/[0.05] p-6 text-sm leading-relaxed text-slate-200" data-testid="sens-frases">
                  <p>Una subida del 5 % en el coste de aprovisionamientos reduce tu EBITDA un {fmtPct(base.frase_aprov5)}.</p>
                  <p className="mt-2">Subir precios un 1 % aumenta tu EBITDA un {fmtPct(base.frase_precio1)}.</p>
                  <p className="mt-2">
                    Cada punto de mejora en el coste de aprovisionamientos equivale aproximadamente a{" "}
                    <strong className="text-gold">{fmt(base.valor_punto_aprov)} €</strong> de valor de tu empresa.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="border border-gold/20 bg-ink-3/60 p-8" data-testid="sens-empty">
              <p className="text-sm leading-relaxed text-slate-400">
                Introduce las cifras de tu último ejercicio y descubre cómo cada palanca afecta a tu EBITDA y al valor de tu empresa.
              </p>
            </div>
          )}
        </div>
      </div>

      {base && (
        <div className="mt-16" data-testid="sens-heatmap">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">
            Mapa de calor del EBITDA · filas: variación de ventas · columnas: coste de aprovisionamientos
          </p>
          <div className="mt-5 overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse text-center text-sm">
              <thead>
                <tr>
                  <th className="border border-gold/15 bg-ink-3/60 px-3 py-2.5 text-left text-[11px] uppercase tracking-[0.12em] text-gold">Ventas \ Aprov.</th>
                  {H_COLS.map((c) => (
                    <th key={c} className="border border-gold/15 bg-ink-3/60 px-3 py-2.5 text-[11px] uppercase tracking-[0.12em] text-gold">{c > 0 ? `+${c}` : c} %</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {H_ROWS.map((r) => (
                  <tr key={r}>
                    <td className="border border-gold/15 bg-ink-3/60 px-3 py-2.5 text-left text-xs text-gold">{r > 0 ? `+${r}` : r} %</td>
                    {H_COLS.map((c) => {
                      const v = heatCell(r, c);
                      const better = v >= base.ebitda;
                      return (
                        <td key={c} className={`border border-gold/15 px-3 py-2.5 ${better ? "bg-emerald-400/10 text-emerald-300" : "bg-red-400/10 text-red-300"}`} data-testid={`sens-cell-${r}-${c}`}>
                          {fmt(v)} €
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {base && (
        <div className="mt-20 border-t border-gold/10 pt-16">
          <div className="mx-auto max-w-2xl">
            {done ? (
              <DonePanel
                testid="sens-done"
                title="Hemos enviado el análisis a tu email."
                subtitle="Un socio de AJG te contactará en 24-48 h para comentar el análisis de forma confidencial."
                extra={
                  <button
                    onClick={() => { navigate("/"); setTimeout(() => document.querySelector("#servicios")?.scrollIntoView({ behavior: "smooth" }), 450); }}
                    data-testid="sens-servicios-link"
                    className="mt-8 border border-gold/60 px-7 py-3.5 text-[12px] font-semibold uppercase tracking-[0.18em] text-gold transition-all hover:bg-gold hover:text-ink"
                  >
                    Conoce nuestro servicio de seguimiento financiero mensual
                  </button>
                }
              />
            ) : (
              <form onSubmit={submitLead} className="space-y-5" data-testid="sens-lead-form">
                <h2 className="font-serif text-3xl text-white">Recibe tu análisis de márgenes completo</h2>
                <p className="text-sm leading-relaxed text-slate-300">
                  Tabla de sensibilidad completa, análisis del punto muerto y palancas de mejora con su efecto estimado en EBITDA y en valor, en PDF.
                </p>
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Nombre">
                    <input data-testid="sens-nombre" value={lead.nombre} onChange={(e) => setLead({ ...lead, nombre: e.target.value })} required className={inputCls} />
                  </Field>
                  <Field label="Empresa">
                    <input data-testid="sens-empresa" value={lead.empresa} onChange={(e) => setLead({ ...lead, empresa: e.target.value })} required className={inputCls} />
                  </Field>
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Email">
                    <input data-testid="sens-email" type="email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} required className={inputCls} />
                  </Field>
                  <Field label="Teléfono">
                    <input data-testid="sens-telefono" type="tel" value={lead.telefono} onChange={(e) => setLead({ ...lead, telefono: e.target.value })} required className={inputCls} />
                  </Field>
                </div>
                <ConsentChecks lead={lead} setLead={setLead} prefix="sens" />
                <GoldButton loading={leadLoading} testid="sens-lead-submit">Recibir mi análisis de márgenes</GoldButton>
                <p className="text-xs leading-relaxed text-slate-500">{LEGAL}</p>
              </form>
            )}
          </div>
        </div>
      )}
    </ToolShell>
  );
}
